import { AuthStorage, type PortalUser } from '../types/auth.types';
import type { StorageChangeCallback, StorageChangeField, TokenStorageStrategy } from './TokenStorageStrategy';

const STORAGE_KEY_TO_FIELD: Record<string, StorageChangeField> = {
  [AuthStorage.weave_token]: 'token',
  [AuthStorage.jti]: 'jti',
  [AuthStorage.user]: 'user',
};

/**
 * Default TokenStorageStrategy implementation for browser environments, using localStorage.
 *
 * Only responsible for getting/setting/removing and notifying of changes via the onStorageChange callback.
 * Caching and other token management logic is handled by the TokenStorage class that wraps this.
 */
export class BrowserTokenStorageStrategy implements TokenStorageStrategy {
  getToken(): string | undefined {
    return localStorage.getItem(AuthStorage.weave_token) ?? undefined;
  }

  setToken(token: string): void {
    localStorage.setItem(AuthStorage.weave_token, token);
  }

  removeToken(): void {
    localStorage.removeItem(AuthStorage.weave_token);
  }

  getJTI(): string | undefined {
    return localStorage.getItem(AuthStorage.jti) ?? undefined;
  }

  setJTI(jti: string): void {
    localStorage.setItem(AuthStorage.jti, jti);
  }

  removeJTI(): void {
    localStorage.removeItem(AuthStorage.jti);
  }

  getTokenReceivedAt(): number | undefined {
    const data = localStorage.getItem(AuthStorage.token_received_at);
    if (!data) return undefined;
    const parsed = parseInt(data, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  setTokenReceivedAt(value: number): void {
    localStorage.setItem(AuthStorage.token_received_at, value.toString());
  }

  removeTokenReceivedAt(): void {
    localStorage.removeItem(AuthStorage.token_received_at);
  }

  getUser(): PortalUser | undefined {
    const data = localStorage.getItem(AuthStorage.user);
    if (!data) return undefined;
    try {
      return JSON.parse(data) as PortalUser;
    } catch {
      return undefined;
    }
  }

  setUser(user: PortalUser): void {
    localStorage.setItem(AuthStorage.user, JSON.stringify(user));
  }

  removeUser(): void {
    localStorage.removeItem(AuthStorage.user);
  }

  onStorageChange(cb: StorageChangeCallback): () => void {
    const handler = (e: StorageEvent) => {
      // Only respond to changes for our relevant keys, and map them to the appropriate field name for the callback.
      const field = e.key ? STORAGE_KEY_TO_FIELD[e.key] : undefined;
      if (field) {
        cb(field, e.newValue);
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }
}
